import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react'
import { FormEventHandler, ReactNode, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import useFeedback from '@/components/Feedback/useFeedback'
import cn from '@/utilities/cn'

import messages from './messages'

type FeedbackDialogProps = {
  children: ({ showDialog }: { showDialog: () => void }) => ReactNode
}

export default function FeedbackDialog({ children }: FeedbackDialogProps) {
  const [open, setOpen] = useState(false)

  const textboxRef = useRef(null)
  const showDialog = () => setOpen(true)
  const hideDialog = () => setOpen(false)

  const { submit, isLoading } = useFeedback({ onSuccess: () => hideDialog() })

  const handleSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault()

    const data = new FormData(event.currentTarget)
    submit({ message: data.get('message')?.toString() })
  }

  return (
    <>
      <Transition show={open}>
        <Dialog as="div" className="relative z-10" initialFocus={textboxRef} onClose={() => {}}>
          <TransitionChild
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center text-center">
              <TransitionChild
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-0 scale-95"
                enterTo="opacity-100 translate-y-0 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 scale-100"
                leaveTo="opacity-0 translate-y-0 scale-95"
              >
                <DialogPanel
                  as="form"
                  className="relative my-8 w-full max-w-xl transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all"
                  onSubmit={handleSubmit}
                >
                  <div className="bg-white p-6 pb-4">
                    <DialogTitle as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      <FormattedMessage {...messages.title} />
                    </DialogTitle>
                    <div className="mt-2">
                      <textarea
                        ref={textboxRef}
                        rows={4}
                        name="message"
                        id="message"
                        className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                          'pointer-events-none opacity-60': isLoading
                        })}
                        required
                        maxLength={2000}
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between bg-gray-50 py-3 px-6">
                    <div>
                      <p className="whitespace-pre-line text-xs text-gray-400">
                        <FormattedMessage {...messages.disclaimer} />
                      </p>
                    </div>

                    <div className="flex flex-row-reverse gap-3">
                      <button
                        type="submit"
                        className={cn(
                          'inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700',
                          { 'pointer-events-none opacity-60': isLoading }
                        )}
                        disabled={isLoading}
                      >
                        <FormattedMessage {...messages.submit} />
                      </button>
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                        onClick={hideDialog}
                      >
                        <FormattedMessage {...messages.cancel} />
                      </button>
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
      {children({ showDialog })}
    </>
  )
}
